import { useRef, useState } from "react";
import { EmptyState } from "@/components/ui/EmptyState";
import { Button } from "@/components/ui/button";
import { DragHandle, dragChrome } from "@/components/ui/DragHandle";
import { NotSavedNote } from "@/components/ui/Note";
import { useUnsavedDraft } from "@/hooks/use-unsaved-draft";
import { Panel } from "@/components/ui/Panel";
import type { FigureResponse } from "@patent-studio/api-types";
import { laneOf } from "@/hooks/use-figure-layout";
import type { FigureGroup } from "@/lib/figure-labels";
import { cn } from "@/lib/utils";
import { SortableFigureRow } from "./FigureRow";

import { useGroupDrag } from "@/components/shared/arrangement-drag";
import { groupHeading, namedGroup } from "@/lib/figure-group-naming";

/** The title, editable where it is shown rather than behind a dialog. */
function GroupTitle({
  group,
  onRetitle,
}: {
  group: FigureGroup;
  onRetitle: (groupId: string, title: string) => Promise<void>;
}) {
  const stored = group.title;
  const [draft, setDraft] = useState(stored);
  const [shown, setShown] = useState(stored);
  const [refused, setRefused] = useState(false);
  useUnsavedDraft(refused && draft !== stored);
  // The title shows before the server answers, and a refusal rolls the cache back. While this
  // field's own save is out, or after it was refused, the field keeps the attorney's words
  // rather than following the cache back to the old title.
  // A count, not a flag: a second save sent while the first is out must not be released by
  // the first one's answer.
  const [sending, setSending] = useState(0);

  // Adjusted while rendering rather than afterwards: an effect would paint the
  // other title once before correcting it.
  if (shown !== stored) {
    setShown(stored);
    if (sending === 0 && !refused) setDraft(stored);
  }

  // Escape leaves the field, and leaving the field is what commits, so without
  // this the key that abandons an edit is the key that saves it. The reset it
  // asks for has not been applied by the time the blur arrives, so the flag
  // carries the intent across rather than the value.
  const abandoningRef = useRef(false);
  const send = () => {
    setSending((count) => count + 1);
    setRefused(false);
    onRetitle(group.group_id, draft).then(
      () => {
        setSending((count) => count - 1);
      },
      () => {
        setSending((count) => count - 1);
        setRefused(true);
      },
    );
  };
  const commit = () => {
    if (abandoningRef.current) {
      abandoningRef.current = false;
      return;
    }
    if (draft !== stored) send();
  };

  return (
    <span className="inline-flex min-w-0 flex-col">
      <input
        value={draft}
        aria-label={`Title of ${namedGroup(group)}`}
        placeholder={groupHeading(group)}
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.currentTarget.blur();
            return;
          }
          if (event.key === "Escape") {
            abandoningRef.current = true;
            setRefused(false);
            setDraft(stored);
            event.currentTarget.blur();
          }
        }}
        // Underlined even when it is not being edited, so the name reads as
        // something that can be changed rather than as a heading that cannot.
        className="min-w-[70px] max-w-full border-0 border-b border-dashed border-border bg-transparent px-1 py-0.5 text-sm font-semibold [field-sizing:content] focus:border-solid focus:border-brand-start focus:outline-none"
      />
      <NotSavedNote failed={refused && draft !== stored} onRetry={send} />
    </span>
  );
}

/**
 * The group's own row: what it is called, how many it holds, and its controls.
 *
 * It carries the group's drop target, rather than the lane below carrying it,
 * because a target covering the whole lane would swallow every drop meant for
 * the members inside it.
 */
function GroupHeader({
  group,
  count,
  targetRef,
  handleRef,
  onRetitle,
  onDissolve,
}: {
  group: FigureGroup;
  count: number;
  targetRef: (element: Element | null) => void;
  handleRef: (element: Element | null) => void;
  onRetitle: (groupId: string, title: string) => Promise<void>;
  onDissolve: (groupId: string) => void;
}) {
  return (
    <div ref={targetRef} className="flex items-center gap-2 pb-2.5">
      <DragHandle
        size="sm"
        handleRef={handleRef}
        data-testid={`figure-group-grip-${group.group_id}`}
        label={`Reorder ${namedGroup(group)}`}
      />
      <span className="flex-none rounded-sm border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-brand-start">
        Group
      </span>
      <GroupTitle group={group} onRetitle={onRetitle} />
      <Button
        variant="secondary"
        size="dense"
        type="button"
        aria-label={`Ungroup ${namedGroup(group)}`}
        onClick={() => {
          onDissolve(group.group_id);
        }}
        className="text-muted-foreground hover:text-destructive"
      >
        Remove
      </Button>
      <span className="ml-auto flex-none text-[11.5px] text-muted-foreground">
        {count === 1 ? "1 figure" : `${String(count)} figures`}
      </span>
    </div>
  );
}

/** The figures the group holds, laid side by side as the design has them. */
function GroupMembers({
  groupId,
  members,
  laneRef,
  figures,
  onDelete,
}: {
  groupId: string;
  members: { file_id: string }[];
  laneRef: (element: Element | null) => void;
  figures: Map<string, FigureResponse>;
  onDelete: (fileId: string) => void;
}) {
  return (
    <ul
      ref={laneRef}
      data-testid={`figure-group-lane-${groupId}`}
      className="flex flex-row flex-wrap items-start gap-2.5"
    >
      {members.length === 0 ? (
        <EmptyState as="li" shape="row" className="w-full py-4 text-xs">
          Drag figures here. An empty group takes no figure number.
        </EmptyState>
      ) : (
        members.map((member, position) => {
          const figure = figures.get(member.file_id);
          return figure === undefined ? null : (
            <SortableFigureRow
              key={member.file_id}
              figure={figure}
              index={position}
              lane={laneOf(groupId)}
              onDelete={onDelete}
            />
          );
        })
      )}
    </ul>
  );
}

/**
 * One group, as a lane its members sit inside.
 *
 * The lane is both a sortable at the top level, so the group can be moved as a
 * unit, and a drop target of its own, so a figure can be moved into it. Those
 * are two different elements: the row's own drop target is its header, because
 * a target covering the whole lane would swallow every drop meant for the
 * members inside it.
 */
export function FigureGroupLane({
  group,
  index,
  figures,
  onDelete,
  onRetitle,
  onDissolve,
  over,
}: {
  group: FigureGroup;
  index: number;
  figures: Map<string, FigureResponse>;
  onDelete: (fileId: string) => void;
  onRetitle: (groupId: string, title: string) => Promise<void>;
  onDissolve: (groupId: string) => void;
  /** What a release right now would land on, anywhere on the screen. */
  over: string | null;
}) {
  const { ref, targetRef, handleRef, isDragging, laneRef } = useGroupDrag(
    group.group_id,
    index,
  );

  const members = group.members ?? [];
  const takesTheDrop =
    over !== null &&
    (over === laneOf(group.group_id) ||
      members.some((member) => member.file_id === over));

  return (
    <Panel
      as="li"
      pad="tight"
      ref={ref}
      data-testid={`figure-group-${group.group_id}`}
      className={cn(
        "transition-colors",
        // Which of the two outcomes a drop will have is otherwise left to be
        // guessed from a few pixels of pointer position, and the pixels do not
        // divide the way the eye does: the lane itself only takes the drop in
        // the gaps between its figures, and a figure aimed anywhere else in
        // there lands on one of them. Both put it in this group, so the group
        // is what lights up, whichever of its parts the pointer found.
        dragChrome({ dragging: isDragging, landing: takesTheDrop }),
      )}
    >
      <GroupHeader
        group={group}
        count={members.length}
        targetRef={targetRef}
        handleRef={handleRef}
        onRetitle={onRetitle}
        onDissolve={onDissolve}
      />

      <GroupMembers
        groupId={group.group_id}
        members={members}
        laneRef={laneRef}
        figures={figures}
        onDelete={onDelete}
      />
    </Panel>
  );
}
